import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { snowflakeService, DateRange } from '../services/snowflakeService';

interface SnowflakeData {
  metrics: any[];
  chartData: any[];
  loading: boolean;
  error: string | null;
}

interface SnowflakeContextType {
  data: SnowflakeData;
  refreshData: (range?: DateRange) => Promise<void>;
  executeQuery: (query: string) => Promise<any>;
}

const SnowflakeContext = createContext<SnowflakeContextType | undefined>(undefined);

interface SnowflakeProviderProps {
  children: ReactNode;
}

export const SnowflakeProvider: React.FC<SnowflakeProviderProps> = ({ children }) => {
  const [data, setData] = useState<SnowflakeData>({
    metrics: [],
    chartData: [],
    loading: false,
    error: null,
  });

  const refreshData = async (range?: DateRange) => {
    setData(prev => ({ ...prev, loading: true, error: null }));
    
    try {
      const [metrics, chartData] = await Promise.all([
        snowflakeService.getMetrics(range),
        snowflakeService.getChartData(range),
      ]);
      
      setData({
        metrics,
        chartData,
        loading: false,
        error: null,
      });
    } catch (error) {
      setData(prev => ({
        ...prev,
        loading: false,
        error: error instanceof Error ? error.message : 'An error occurred',
      }));
    }
  };

  const executeQuery = async (query: string) => {
    try {
      return await snowflakeService.executeQuery(query);
    } catch (error) {
      throw error;
    }
  };

  useEffect(() => {
    // Initialize with default last 7 days range
    const fmt = (d: Date) => {
      const mm = String(d.getMonth() + 1).padStart(2, '0');
      const dd = String(d.getDate()).padStart(2, '0');
      const yyyy = d.getFullYear();
      return `${mm}/${dd}/${yyyy}`;
    };

    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - 7);
    const startStr = fmt(start);
    const endStr = fmt(end);
    refreshData({ startDate: startStr, endDate: endStr });
  }, []);

  const value = {
    data,
    refreshData,
    executeQuery,
  };

  return (
    <SnowflakeContext.Provider value={value}>
      {children}
    </SnowflakeContext.Provider>
  );
};

export const useSnowflake = () => {
  const context = useContext(SnowflakeContext);
  if (context === undefined) {
    throw new Error('useSnowflake must be used within a SnowflakeProvider');
  }
  return context;
};
